import { nextMonday } from 'date-fns';
import { isEmpty, isEqual } from 'lodash-es';
import { useObservableState, useSubscription } from 'observable-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useFormContext } from 'react-hook-form';

import type { FrequencyWeighting } from '@/graphql/types';
import { LdenGuideline } from '@/graphql/types';
import { getUnitWithFrequencyWeighting } from '../../../graph/graph-elements/sound-plugin/unit-formatter';
import { schema } from '../../../schema';
import ErrorContainer from '../../errorContainer/ErrorContainer';
import LdenCalendar from '../../ldenCalendar/LdenCalendar';
import type { TimeWindow } from '../../ldenCalendar/utils';
import {
    calendarTimezone,
    extendTemplate,
    formatTemplateData,
    templateStartInUtc,
} from '../../ldenCalendar/utils';
import { applyModifierForTimezone } from '../../ldenCalendar/weekPicker/WeekPicker';
import { OVERRIDES_NAME, useWatchObservable } from '../utils';

const inViewStartDate = applyModifierForTimezone(templateStartInUtc, calendarTimezone, nextMonday);

interface LdenGuidelinePreviewWrapperProps {
    data: TimeWindow[];
    buildMode?: boolean;
    customTemplate: TimeWindow[];
    inViewStartDate: Date;
    dayStartOffset: number;
    timezone: string;
    unit?: string;
    currentDate: Date;
    changeTemplate?: (template: TimeWindow[]) => void;
    deleteCustomTemplate?: () => void;
}

export function LdenGuidelinePreviewWrapper(props: LdenGuidelinePreviewWrapperProps) {
    const data = useMemo(
        () =>
            formatTemplateData(
                extendTemplate(props.buildMode ? props.customTemplate : props.data, 3)
            ),
        [props.customTemplate, props.data, props.buildMode]
    );

    return <LdenCalendar {...props} data={data} />;
}

function LdenGuidelinePreviewField() {
    const { setValue } = useFormContext();
    const [customTemplate, setCustomTemplate] = useState<TimeWindow[]>([]);
    const [previousGuideline, setPreviousGuideline] = useState<LdenGuideline | null>(null);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const ldenGuideline$ = useWatchObservable<LdenGuideline>('ldenGuideline');
    const ldenGuideline = useObservableState(ldenGuideline$);

    const ldenSchedules$ = useWatchObservable<TimeWindow[]>('notificationConfig.ldenSchedules');
    const ldenSchedules = useObservableState(ldenSchedules$, []);

    useEffect(() => {
        // Only set when schedule is different from what the form already knows.
        if (!isEqual(ldenSchedules, customTemplate)) {
            setValue('notificationConfig.ldenSchedules', customTemplate);
        }
    }, [ldenSchedules, customTemplate, setValue]);

    const frequencyWeighting$ = useWatchObservable<FrequencyWeighting>('frequencyWeighting');
    const frequencyWeighting = useObservableState(frequencyWeighting$);

    const currentDate = useMemo(() => new Date(), []);
    const isCustom = useMemo(
        () => ldenGuideline === LdenGuideline.LdenGuidelineCustom,
        [ldenGuideline]
    );

    const deleteCustomTemplate = useCallback(() => {
        setCustomTemplate([]);
        setValue('notificationConfig.alarmSchedules', []);
    }, [setCustomTemplate, setValue]);

    useSubscription(useWatchObservable(`${OVERRIDES_NAME}.ldenGuideline`), {
        next(value) {
            if (value !== undefined) {
                setValue(`${OVERRIDES_NAME}.notificationConfig.ldenSchedules`, value);
            }
        },
    });

    useEffect(() => {
        if (!initialLoadComplete && ldenGuideline) {
            if (isCustom) {
                setCustomTemplate(ldenSchedules);
            }
            setInitialLoadComplete(true);
            setPreviousGuideline(ldenGuideline);
        } else if (initialLoadComplete && ldenGuideline !== previousGuideline) {
            setPreviousGuideline(ldenGuideline ?? null);
        }
    }, [
        setCustomTemplate,
        ldenGuideline,
        isCustom,
        ldenSchedules,
        initialLoadComplete,
        previousGuideline,
    ]);

    const unit = useMemo(() => {
        const config = frequencyWeighting
            ? {
                  frequencyWeighting,
              }
            : {};

        return getUnitWithFrequencyWeighting(config);
    }, [frequencyWeighting]);

    const template = useMemo(() => {
        if (!ldenGuideline) {
            return [];
        }

        if (isCustom) {
            return customTemplate;
        }

        return schema.ldenGuidelines[ldenGuideline] as unknown as TimeWindow[];
    }, [ldenGuideline, isCustom, customTemplate]);

    if (isEmpty(template) && !isCustom) {
        return null;
    }

    return (
        <ErrorBoundary fallback={<ErrorContainer />}>
            <LdenGuidelinePreviewWrapper
                data={template}
                buildMode={isCustom}
                // By extending the template for 3 weeks and setting the view start date
                // to the second week, we ensure that any time windows starting before or
                // ending after the initial week are covered. This prevents empty spots at
                // the beginning and end of the displayed week.
                inViewStartDate={inViewStartDate}
                dayStartOffset={7}
                timezone={calendarTimezone}
                unit={unit}
                currentDate={currentDate}
                changeTemplate={setCustomTemplate}
                customTemplate={customTemplate}
                deleteCustomTemplate={deleteCustomTemplate}
            />
        </ErrorBoundary>
    );
}

export default LdenGuidelinePreviewField;
